/**
 * Mejoras permanentes (Daño / Vida / Defensa) compradas en el lobby con
 * monedas. Curva de costo exponencial suave: `costo = baseCost *
 * costGrowth^nivelActual` (definida por upgrade en `data/upgrades.json`,
 * `costGrowth` = 1.35 para las tres). Los niveles comprados se guardan en
 * `MetaState.upgrades` y se aplican al `RunState` inicial de cada run nueva.
 */

import type { MetaState, RunState, UpgradeDef } from '../core/types';
import { upgrades as upgradeDefs } from '../data';

export type UpgradeId = UpgradeDef['id'];

function getDef(id: UpgradeId, defs: UpgradeDef[] = upgradeDefs): UpgradeDef {
  const def = defs.find((u) => u.id === id);
  if (!def) throw new Error(`upgrades: no existe la definición "${id}"`);
  return def;
}

/** Costo en monedas para comprar el próximo nivel, partiendo de `currentLevel`. */
export function getUpgradeCost(id: UpgradeId, currentLevel: number, defs: UpgradeDef[] = upgradeDefs): number {
  const def = getDef(id, defs);
  return Math.round(def.baseCost * def.costGrowth ** currentLevel);
}

/** ¿Ya llegó al nivel máximo definido para este upgrade? */
export function isUpgradeMaxed(id: UpgradeId, currentLevel: number, defs: UpgradeDef[] = upgradeDefs): boolean {
  return currentLevel >= getDef(id, defs).maxLevel;
}

/** ¿El jugador puede pagar el próximo nivel de este upgrade ahora mismo? */
export function canAffordUpgrade(meta: MetaState, id: UpgradeId, defs: UpgradeDef[] = upgradeDefs): boolean {
  const level = meta.upgrades[id];
  if (isUpgradeMaxed(id, level, defs)) return false;
  return meta.coins >= getUpgradeCost(id, level, defs);
}

/**
 * Compra un nivel de mejora. Si no se puede pagar o ya está al máximo,
 * devuelve el mismo `meta` sin cambios (el llamador puede comparar
 * referencias o releer `canAffordUpgrade` antes de mostrar el botón activo).
 */
export function purchaseUpgrade(meta: MetaState, id: UpgradeId, defs: UpgradeDef[] = upgradeDefs): MetaState {
  if (!canAffordUpgrade(meta, id, defs)) return meta;
  const level = meta.upgrades[id];
  const cost = getUpgradeCost(id, level, defs);
  return {
    ...meta,
    coins: meta.coins - cost,
    upgrades: { ...meta.upgrades, [id]: level + 1 },
  };
}

/**
 * Aplica las mejoras permanentes compradas al estado inicial de una run
 * nueva: Daño → `stats.atkPct`, Defensa → `stats.armor`, Vida → `maxHp`/`hp`
 * (la run siempre arranca con vida llena).
 */
export function applyUpgradesToRunState(
  runState: RunState,
  meta: MetaState,
  defs: UpgradeDef[] = upgradeDefs
): RunState {
  const atkDef = getDef('atk', defs);
  const hpDef = getDef('hp', defs);
  const defDef = getDef('def', defs);

  const hpBonus = hpDef.valuePerLevel * meta.upgrades.hp;
  const atkBonus = atkDef.valuePerLevel * meta.upgrades.atk;
  const armorBonus = defDef.valuePerLevel * meta.upgrades.def;
  const maxHp = runState.maxHp + hpBonus;

  return {
    ...runState,
    maxHp,
    hp: maxHp,
    stats: {
      ...runState.stats,
      atkPct: runState.stats.atkPct + atkBonus,
      armor: runState.stats.armor + armorBonus,
    },
  };
}
